import Ionicons from "@react-native-vector-icons/ionicons";

type IconName = keyof typeof Ionicons.glyphMap;

export const getVehicleTypeLabel = (type: string) => {
  switch (type) {
    case "under_3.5t":
      return "Under 3.5 tonnes";
    case "3.5-7.5t":
      return "3.5T - 7.5 tonnes";
    case "over_7.5t":
      return "Over 7.5 tonnes";
    default:
      return "Under 3.5 tonnes";
  }
};

export const VEHICLE_CATEGORIES = [
  { value: "under_3.5t", label: "Under 3.5t" },
  { value: "3.5-7.5t", label: "3.5 - 7.5t" },
  { value: "over_7.5t", label: "Over 7.5t" },
];

// ─── Vehicle Types ────────────────────────────────────────────────────────────
export const VEHICLE_TYPES = [
  { value: "car", label: "Car", icon: "car" as const },
  { value: "van", label: "Van", icon: "car" as const },
  { value: "motorhome", label: "Motorhome", icon: "bus" as const },
  { value: "caravan", label: "Caravan", icon: "car-sport" as const },
  { value: "truck", label: "Truck", icon: "bus" as const },
  { value: "bike", label: "Bike", icon: "bicycle" as const },
  {
    value: "machinery",
    label: "Farm Machinery/Equipment",
    icon: "construct" as const,
  },
  { value: "plant", label: "Plant", icon: "construct" as const },
];

// ─── Body Types per Vehicle Type ──────────────────────────────────────────────
export const VEHICLE_BODY_TYPES: Record<
  string,
  { value: string; label: string; icon?: IconName }[]
> = {
  car: [
    { value: "convertible", label: "Convertible", icon: "car-sport" },
    { value: "coupe", label: "Coupe", icon: "car" },
    { value: "estate", label: "Estate", icon: "car" },
    { value: "hatchback", label: "Hatchback", icon: "car" },
    { value: "mpv", label: "MPV", icon: "car" },
    { value: "pickup", label: "Pickup", icon: "car" },
    { value: "saloon", label: "Saloon", icon: "car" },
    { value: "suv", label: "SUV", icon: "car" },
    { value: "crossover", label: "Crossover", icon: "car" },
    { value: "sedan", label: "Sedan", icon: "car" },
  ],
  van: [
    { value: "panel_van", label: "Panel Van", icon: "car" },
    { value: "minivan", label: "Minivan", icon: "car" },
    { value: "crew_van", label: "Crew Van", icon: "car" },
    { value: "chassis_cab", label: "Chassis Cab", icon: "car" },
    { value: "luton", label: "Luton", icon: "car" },
    { value: "dropside", label: "Dropside", icon: "car" },
    { value: "tipper", label: "Tipper", icon: "car" },
    { value: "curtainsider", label: "Curtainsider", icon: "car" },
  ],
  motorhome: [
    { value: "a_class", label: "A-Class", icon: "bus" },
    { value: "coachbuilt", label: "Coachbuilt", icon: "bus" },
    { value: "overcab", label: "Overcab / Alcove", icon: "bus" },
    { value: "campervan", label: "Campervan", icon: "bus" },
    { value: "low_profile", label: "Low Profile", icon: "bus" },
  ],
  caravan: [
    { value: "touring", label: "Touring Caravan", icon: "car-sport" },
    { value: "static", label: "Static Caravan", icon: "car-sport" },
    { value: "folding", label: "Folding / Pop-top", icon: "car-sport" },
    { value: "fifth_wheel", label: "Fifth Wheel", icon: "car-sport" },
  ],
  truck: [
    { value: "flatbed", label: "Flatbed", icon: "bus" },
    { value: "box_truck", label: "Box / Curtainsider", icon: "bus" },
    { value: "tipper_truck", label: "Tipper", icon: "bus" },
    { value: "skip_loader", label: "Skip Loader", icon: "bus" },
    { value: "tanker", label: "Tanker", icon: "bus" },
    { value: "refrigerated", label: "Refrigerated", icon: "bus" },
    { value: "low_loader", label: "Low Loader", icon: "bus" },
    { value: "articulated", label: "Articulated / HGV", icon: "bus" },
  ],
  bike: [
    { value: "scooter", label: "Scooter / Moped", icon: "bicycle" },
    { value: "naked", label: "Naked / Street", icon: "bicycle" },
    { value: "sports", label: "Sports / Supersport", icon: "bicycle" },
    { value: "cruiser", label: "Cruiser", icon: "bicycle" },
    { value: "adventure", label: "Adventure / Touring", icon: "bicycle" },
    { value: "enduro", label: "Enduro / Off-Road", icon: "bicycle" },
    { value: "electric_bike", label: "Electric Bike", icon: "bicycle" },
    { value: "trike", label: "Trike", icon: "bicycle" },
  ],
  machinery: [
    { value: "tractor", label: "Tractor", icon: "construct" },
    { value: "harvester", label: "Harvester", icon: "construct" },
    { value: "other", label: "Other", icon: "construct" },
  ],
  plant: [
    { value: "excavator", label: "Excavator", icon: "construct" },
    { value: "forklift", label: "Forklift", icon: "construct" },
    { value: "other", label: "Other", icon: "construct" },
  ],
};

// ─── Transmission Options ─────────────────────────────────────────────────────
export const TRANSMISSION_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
  { value: "semi_automatic", label: "Semi-Automatic" },
];

// ─── Year / Colour / Fuel ─────────────────────────────────────────────────────
const currentYear = new Date().getFullYear();

export const VEHICLE_YEARS = Array.from(
  { length: currentYear - 1979 },
  (_, index) => {
    const year = String(currentYear - index);
    return { value: year, label: year };
  },
);

export const VEHICLE_COLOURS = [
  { value: "white", label: "White" },
  { value: "black", label: "Black" },
  { value: "silver", label: "Silver" },
  { value: "grey", label: "Grey" },
  { value: "gray", label: "Gray" },
  { value: "blue", label: "Blue" },
  { value: "red", label: "Red" },
  { value: "green", label: "Green" },
  { value: "yellow", label: "Yellow" },
  { value: "orange", label: "Orange" },
  { value: "brown", label: "Brown" },
  { value: "beige", label: "Beige" },
  { value: "gold", label: "Gold" },
  { value: "purple", label: "Purple" },
  { value: "other", label: "Other" },
];

export const FUEL_TYPES = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Hybrid" },
  { value: "lpg", label: "LPG" },
  { value: "other", label: "Other" },
];

export const MISFUEL_FUEL_TYPES = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "lpg", label: "LPG" },
  { value: "other", label: "Other" },
];

export const WRONG_FUEL_LITRE_OPTIONS = [
  { value: "5", label: "5 litres" },
  { value: "10", label: "10 litres" },
  { value: "15", label: "15 litres" },
  { value: "20", label: "20 litres" },
  { value: "30", label: "30 litres" },
  { value: "40", label: "40 litres" },
  { value: "50", label: "50 litres" },
  { value: "75", label: "75 litres" },
  { value: "100", label: "100 litres" },
  { value: "125", label: "125 litres" },
  { value: "130", label: "130 litres" },
  { value: "140", label: "140 litres" },
  { value: "145", label: "145 litres" },
  { value: "150", label: "150 litres" },
  { value: "155", label: "155 litres" },
  { value: "160", label: "160 litres" },
  { value: "165", label: "165 litres" },
  { value: "170", label: "170 litres" },
  { value: "175", label: "175 litres" },
  { value: "180", label: "180 litres" },
  { value: "185", label: "185 litres" },
  { value: "190", label: "190 litres" },
  { value: "195", label: "195 litres" },
  { value: "200", label: "200 litres" },
];

export const LOCATION_TYPES = [
  { value: "roadside", label: "Roadside" },
  { value: "driveway", label: "Driveway" },
  { value: "car_park", label: "Car park" },
  { value: "motorway", label: "Motorway" },
];

// ─── Wheelbase Options ────────────────────────────────────────────────────────
export const WHEELBASE_OPTIONS = [
  { value: "swb", label: "SWB – Short Wheelbase" },
  { value: "mwb", label: "MWB – Medium Wheelbase" },
  { value: "lwb", label: "LWB – Long Wheelbase" },
];

// ─── Vehicle Weight ───────────────────────────────────────────────────────────
// (Free-text field; helper labels only)
export const VEHICLE_WEIGHT_HINT = "e.g. 1200 kg";

// ─── Vehicle Damage Types ─────────────────────────────────────────────────────
export const VEHICLE_DAMAGE_TYPES = [
  { value: "minor_damage", label: "Minor Damage" },
  { value: "moderate_damage", label: "Moderate Damage" },
  { value: "severe_damage", label: "Severe Damage" },
  { value: "total_loss", label: "Total Loss" },
  { value: "mechanical_failure", label: "Mechanical Failure" },
  { value: "other", label: "Other" },
];

// ─── Non-Drivable Reasons ─────────────────────────────────────────────────────
export const NON_DRIVABLE_REASONS = [
  { label: "Mechanical/Electrical Failure", value: "mechanical_failure" },
  { label: "Accident Damage", value: "accident_damage" },
  { label: "Accessibility", value: "accessibility" },
  { label: "Other", value: "other" },
];

// ─── Vehicle Makes ────────────────────────────────────────────────────────────
export const VEHICLE_MAKES = [
  { value: "abarth", label: "Abarth" },
  { value: "ac", label: "AC" },
  { value: "alfa_romeo", label: "Alfa Romeo" },
  { value: "alpine", label: "Alpine" },
  { value: "aston_martin", label: "Aston Martin" },
  { value: "audi", label: "Audi" },
  { value: "bentley", label: "Bentley" },
  { value: "bmw", label: "BMW" },
  { value: "bugatti", label: "Bugatti" },
  { value: "buick", label: "Buick" },
  { value: "cadillac", label: "Cadillac" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "chrysler", label: "Chrysler" },
  { value: "citroen", label: "Citroën" },
  { value: "cupra", label: "CUPRA" },
  { value: "dacia", label: "Dacia" },
  { value: "ds", label: "DS Automobiles" },
  { value: "ferrari", label: "Ferrari" },
  { value: "fiat", label: "Fiat" },
  { value: "ford", label: "Ford" },
  { value: "genesis", label: "Genesis" },
  { value: "honda", label: "Honda" },
  { value: "hyundai", label: "Hyundai" },
  { value: "infiniti", label: "Infiniti" },
  { value: "isuzu", label: "Isuzu" },
  { value: "jaguar", label: "Jaguar" },
  { value: "jeep", label: "Jeep" },
  { value: "kia", label: "Kia" },
  { value: "lamborghini", label: "Lamborghini" },
  { value: "land_rover", label: "Land Rover" },
  { value: "lexus", label: "Lexus" },
  { value: "lotus", label: "Lotus" },
  { value: "maserati", label: "Maserati" },
  { value: "mazda", label: "Mazda" },
  { value: "mclaren", label: "McLaren" },
  { value: "mercedes_benz", label: "Mercedes-Benz" },
  { value: "mg", label: "MG" },
  { value: "mini", label: "MINI" },
  { value: "mitsubishi", label: "Mitsubishi" },
  { value: "nissan", label: "Nissan" },
  { value: "opel", label: "Opel" },
  { value: "peugeot", label: "Peugeot" },
  { value: "porsche", label: "Porsche" },
  { value: "ram", label: "RAM" },
  { value: "renault", label: "Renault" },
  { value: "rolls_royce", label: "Rolls-Royce" },
  { value: "seat", label: "SEAT" },
  { value: "skoda", label: "Škoda" },
  { value: "smart", label: "Smart" },
  { value: "subaru", label: "Subaru" },
  { value: "suzuki", label: "Suzuki" },
  { value: "tesla", label: "Tesla" },
  { value: "toyota", label: "Toyota" },
  { value: "vauxhall", label: "Vauxhall" },
  { value: "volkswagen", label: "Volkswagen" },
  { value: "volvo", label: "Volvo" },
  { value: "other", label: "Other" },
];

// ─── Vehicle Models by Make ───────────────────────────────────────────────────
export const VEHICLE_MODELS_BY_MAKE: Record<
  string,
  { value: string; label: string }[]
> = {
  audi: [
    { value: "a1", label: "A1" },
    { value: "a3", label: "A3" },
    { value: "a4", label: "A4" },
    { value: "a5", label: "A5" },
    { value: "a6", label: "A6" },
    { value: "a7", label: "A7" },
    { value: "a8", label: "A8" },
    { value: "q2", label: "Q2" },
    { value: "q3", label: "Q3" },
    { value: "q5", label: "Q5" },
    { value: "q7", label: "Q7" },
    { value: "q8", label: "Q8" },
    { value: "rs3", label: "RS3" },
    { value: "rs6", label: "RS6" },
    { value: "tt", label: "TT" },
    { value: "e_tron", label: "e-tron" },
  ],
  bmw: [
    { value: "1_series", label: "1 Series" },
    { value: "2_series", label: "2 Series" },
    { value: "3_series", label: "3 Series" },
    { value: "4_series", label: "4 Series" },
    { value: "5_series", label: "5 Series" },
    { value: "6_series", label: "6 Series" },
    { value: "7_series", label: "7 Series" },
    { value: "8_series", label: "8 Series" },
    { value: "x1", label: "X1" },
    { value: "x2", label: "X2" },
    { value: "x3", label: "X3" },
    { value: "x4", label: "X4" },
    { value: "x5", label: "X5" },
    { value: "x6", label: "X6" },
    { value: "x7", label: "X7" },
    { value: "m3", label: "M3" },
    { value: "m5", label: "M5" },
    { value: "i3", label: "i3" },
    { value: "i4", label: "i4" },
    { value: "ix", label: "iX" },
  ],
  ford: [
    { value: "fiesta", label: "Fiesta" },
    { value: "focus", label: "Focus" },
    { value: "mondeo", label: "Mondeo" },
    { value: "mustang", label: "Mustang" },
    { value: "puma", label: "Puma" },
    { value: "kuga", label: "Kuga" },
    { value: "explorer", label: "Explorer" },
    { value: "ranger", label: "Ranger" },
    { value: "transit", label: "Transit" },
    { value: "transit_custom", label: "Transit Custom" },
    { value: "galaxy", label: "Galaxy" },
    { value: "s_max", label: "S-MAX" },
    { value: "edge", label: "Edge" },
    { value: "mach_e", label: "Mustang Mach-E" },
  ],
  mercedes_benz: [
    { value: "a_class", label: "A-Class" },
    { value: "b_class", label: "B-Class" },
    { value: "c_class", label: "C-Class" },
    { value: "e_class", label: "E-Class" },
    { value: "s_class", label: "S-Class" },
    { value: "cla", label: "CLA" },
    { value: "cls", label: "CLS" },
    { value: "gla", label: "GLA" },
    { value: "glb", label: "GLB" },
    { value: "glc", label: "GLC" },
    { value: "gle", label: "GLE" },
    { value: "gls", label: "GLS" },
    { value: "amg_gt", label: "AMG GT" },
    { value: "eqc", label: "EQC" },
    { value: "eqs", label: "EQS" },
    { value: "sprinter", label: "Sprinter" },
    { value: "vito", label: "Vito" },
  ],
  toyota: [
    { value: "aygo", label: "Aygo" },
    { value: "yaris", label: "Yaris" },
    { value: "corolla", label: "Corolla" },
    { value: "camry", label: "Camry" },
    { value: "c_hr", label: "C-HR" },
    { value: "rav4", label: "RAV4" },
    { value: "highlander", label: "Highlander" },
    { value: "land_cruiser", label: "Land Cruiser" },
    { value: "hilux", label: "Hilux" },
    { value: "supra", label: "Supra" },
    { value: "prius", label: "Prius" },
    { value: "gr86", label: "GR86" },
    { value: "proace", label: "Proace" },
  ],
  volkswagen: [
    { value: "polo", label: "Polo" },
    { value: "golf", label: "Golf" },
    { value: "passat", label: "Passat" },
    { value: "arteon", label: "Arteon" },
    { value: "tiguan", label: "Tiguan" },
    { value: "touareg", label: "Touareg" },
    { value: "t_roc", label: "T-Roc" },
    { value: "t_cross", label: "T-Cross" },
    { value: "id3", label: "ID.3" },
    { value: "id4", label: "ID.4" },
    { value: "id5", label: "ID.5" },
    { value: "transporter", label: "Transporter" },
    { value: "caddy", label: "Caddy" },
    { value: "crafter", label: "Crafter" },
    { value: "amarok", label: "Amarok" },
  ],
  vauxhall: [
    { value: "corsa", label: "Corsa" },
    { value: "astra", label: "Astra" },
    { value: "insignia", label: "Insignia" },
    { value: "mokka", label: "Mokka" },
    { value: "crossland", label: "Crossland" },
    { value: "grandland", label: "Grandland" },
    { value: "vivaro", label: "Vivaro" },
    { value: "movano", label: "Movano" },
    { value: "combo", label: "Combo" },
  ],
  honda: [
    { value: "jazz", label: "Jazz" },
    { value: "civic", label: "Civic" },
    { value: "accord", label: "Accord" },
    { value: "cr_v", label: "CR-V" },
    { value: "hr_v", label: "HR-V" },
    { value: "zr_v", label: "ZR-V" },
    { value: "e", label: "Honda e" },
    { value: "type_r", label: "Civic Type R" },
  ],
  hyundai: [
    { value: "i10", label: "i10" },
    { value: "i20", label: "i20" },
    { value: "i30", label: "i30" },
    { value: "i40", label: "i40" },
    { value: "ioniq", label: "IONIQ" },
    { value: "ioniq_5", label: "IONIQ 5" },
    { value: "ioniq_6", label: "IONIQ 6" },
    { value: "kona", label: "Kona" },
    { value: "tucson", label: "Tucson" },
    { value: "santa_fe", label: "Santa Fe" },
    { value: "nexo", label: "Nexo" },
  ],
  kia: [
    { value: "picanto", label: "Picanto" },
    { value: "rio", label: "Rio" },
    { value: "ceed", label: "Ceed" },
    { value: "stinger", label: "Stinger" },
    { value: "stonic", label: "Stonic" },
    { value: "niro", label: "Niro" },
    { value: "sportage", label: "Sportage" },
    { value: "sorento", label: "Sorento" },
    { value: "ev6", label: "EV6" },
    { value: "ev9", label: "EV9" },
    { value: "telluride", label: "Telluride" },
  ],
  nissan: [
    { value: "micra", label: "Micra" },
    { value: "juke", label: "Juke" },
    { value: "qashqai", label: "Qashqai" },
    { value: "x_trail", label: "X-Trail" },
    { value: "leaf", label: "Leaf" },
    { value: "ariya", label: "Ariya" },
    { value: "navara", label: "Navara" },
    { value: "gt_r", label: "GT-R" },
    { value: "nv200", label: "NV200" },
    { value: "nv400", label: "NV400" },
  ],
  land_rover: [
    { value: "defender", label: "Defender" },
    { value: "discovery", label: "Discovery" },
    { value: "discovery_sport", label: "Discovery Sport" },
    { value: "freelander", label: "Freelander" },
    { value: "range_rover", label: "Range Rover" },
    { value: "range_rover_sport", label: "Range Rover Sport" },
    { value: "range_rover_evoque", label: "Range Rover Evoque" },
    { value: "range_rover_velar", label: "Range Rover Velar" },
  ],
  jaguar: [
    { value: "xe", label: "XE" },
    { value: "xf", label: "XF" },
    { value: "xj", label: "XJ" },
    { value: "e_pace", label: "E-PACE" },
    { value: "f_pace", label: "F-PACE" },
    { value: "i_pace", label: "I-PACE" },
    { value: "f_type", label: "F-TYPE" },
  ],
  porsche: [
    { value: "911", label: "911" },
    { value: "718", label: "718 Cayman / Boxster" },
    { value: "cayenne", label: "Cayenne" },
    { value: "macan", label: "Macan" },
    { value: "panamera", label: "Panamera" },
    { value: "taycan", label: "Taycan" },
  ],
  tesla: [
    { value: "model_s", label: "Model S" },
    { value: "model_3", label: "Model 3" },
    { value: "model_x", label: "Model X" },
    { value: "model_y", label: "Model Y" },
    { value: "cybertruck", label: "Cybertruck" },
  ],
  renault: [
    { value: "clio", label: "Clio" },
    { value: "megane", label: "Mégane" },
    { value: "captur", label: "Captur" },
    { value: "kadjar", label: "Kadjar" },
    { value: "koleos", label: "Koleos" },
    { value: "zoe", label: "Zoé" },
    { value: "trafic", label: "Trafic" },
    { value: "master", label: "Master" },
  ],
  peugeot: [
    { value: "208", label: "208" },
    { value: "308", label: "308" },
    { value: "508", label: "508" },
    { value: "2008", label: "2008" },
    { value: "3008", label: "3008" },
    { value: "5008", label: "5008" },
    { value: "e_208", label: "e-208" },
    { value: "e_2008", label: "e-2008" },
    { value: "partner", label: "Partner" },
    { value: "expert", label: "Expert" },
    { value: "boxer", label: "Boxer" },
  ],
  // Bike makes
  honda_bikes: [
    { value: "cbr600rr", label: "CBR600RR" },
    { value: "cbr1000rr", label: "CBR1000RR Fireblade" },
    { value: "cb500f", label: "CB500F" },
    { value: "africa_twin", label: "Africa Twin" },
    { value: "crf", label: "CRF" },
    { value: "pcx", label: "PCX" },
  ],
};

// Helper: get model list for a given make value (returns empty array if not found)
export function getModelsForMake(
  makeValue: string,
): { value: string; label: string }[] {
  const models = VEHICLE_MODELS_BY_MAKE[makeValue] ?? [];
  if (models.length === 0 || !models.find((m) => m.value === "other")) {
    return [...models, { value: "other", label: "Other" }];
  }
  return models;
}

// Helper: get body types for a given vehicle type value
export function getBodyTypesForVehicleType(
  vehicleType: string,
): { value: string; label: string; icon?: IconName }[] {
  return VEHICLE_BODY_TYPES[vehicleType] ?? [];
}

// ─── Tyre Positions ───────────────────────────────────────────────────────────
export const TYRE_POSITIONS = [
  { value: "fl", label: "Front Left (FL)" },
  { value: "fr", label: "Front Right (FR)" },
  { value: "rl", label: "Rear Left (RL)" },
  { value: "rr", label: "Rear Right (RR)" },
  { value: "all", label: "All Tyres" },
];

// ─── Special Issues ───────────────────────────────────────────────────────────
export const SPECIAL_ISSUES = [
  { value: "pet_hair", label: "Pet Hair" },
  { value: "smoke", label: "Smoke" },
  { value: "stains", label: "Stains" },
  { value: "vomit", label: "Vomit" },
  { value: "none", label: "None" },
];
